import React, { useContext, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import DefaultLayout from "../layouts/DefaultLayout";
import Seo from "./common/Seo";
import { AccountContext } from "../store/AccountContext";
import { OverviewContext } from "../store/OverviewContext";
import { captureAttribution } from "../utils/attribution";
import {
  buildYouTubeEmbedUrl,
  buildYouTubeWatchUrl,
  findYouTubeVideoId,
  isCatalogueUuid,
  isYouTubeVideoId,
  parseStartSeconds,
  youTubeThumbnailUrl,
} from "../utils/youtube";
import { logYouTubeLandingEvent } from "../utils/youtubeAnalytics";

// Free-on-YouTube landing page: /youtube/<id> (#588 page, #590 link target).
//
// link.farmflix.tv/youtube/<id> opens the app when it is installed; anyone
// else is sent here through /link/* (LinkResolver + utils/linkRoutes.ts).
// The id is a YouTube video id (plays now) or a FarmFlix catalogue uuid
// (plays once the overview carries a youtube section, #558). See
// utils/youtube.ts.
//
// Rules from the spec (docs/specs/youtube-crossover-ux-frs.md, FFCustomerApp)
// and #588:
//   - free and open to everyone: no login or payment in front of the video;
//   - the official embed with standard controls, nothing laid over it, and
//     no FarmFlix promotion inside the player box. The join and app buttons
//     sit below it;
//   - a missing or unknown id goes quietly Home. Never an error page.
//
// TODO(#587): "Home" should show the Free-on-YouTube rail once it exists.

// DefaultLayout centres its children (alignItems: center), so without the
// width this box shrinks to the Container and the layout's lighter grey
// shows either side.
const backgroundSx: SxProps<Theme> = {
  bgcolor: "background.ffNavy",
  width: "100%",
  flexGrow: 1,
  py: { xs: 3, md: 5 },
};

// 16:9 box that holds the embed and nothing else.
const playerBoxSx: SxProps<Theme> = {
  position: "relative",
  width: "100%",
  paddingTop: "56.25%",
  bgcolor: "#000",
  "& iframe": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: 0,
  },
};

const headingSx: SxProps<Theme> = {
  color: "common.white",
  fontWeight: 600,
  fontSize: { xs: "1.6rem", md: "2.2rem" },
  mb: 2,
};

const bodySx: SxProps<Theme> = { color: "#bbb", mb: 2 };

const actionsSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: 2,
  mt: 2,
};

// Same pin as Onboarding/TryButton: the v5 theme gives every Button a lime
// background, and the default primary text colour is the same lime.
const buttonSx: SxProps<Theme> = { color: "text.primary" };

// Outlined buttons sit on the dark page with no fill, so text.primary (dark)
// would be invisible. White text and border, like the header's Log in button.
// Repeated under &:hover, because the theme's outlined hover rule sets them
// back to near-black and its :hover selector beats the base sx colour.
const outlinedButtonSx: SxProps<Theme> = {
  color: "common.white",
  borderColor: "common.white",
  "&:hover": {
    color: "common.white",
    borderColor: "common.white",
  },
};

const upsellSx: SxProps<Theme> = {
  mt: 4,
  p: { xs: 2, md: 3 },
  borderRadius: 1,
  bgcolor: "rgba(255, 255, 255, 0.05)",
};

const YouTubeLanding = () => {
  const { id } = useParams();
  const { search } = useLocation();
  const { overview, loading, error } = useContext(OverviewContext);
  const {
    loggedIn,
    validated,
    loading: accountLoading,
  } = useContext(AccountContext);
  // loggedIn reads false until the stored session has been checked, so the
  // member/non-member buttons wait for that; the player does not.
  const accountSettled = validated && !accountLoading;

  const idKind = isYouTubeVideoId(id)
    ? "youtube"
    : isCatalogueUuid(id)
      ? "uuid"
      : undefined;

  // A uuid can only be resolved from the overview; wait for it. A failed
  // overview counts as "not found", as on the campaign page.
  const overviewSettled = !!overview || (!!error && !loading);
  const videoId =
    idKind === "youtube"
      ? id
      : idKind === "uuid" && overview
        ? findYouTubeVideoId(overview, id as string)
        : undefined;
  const startSeconds = parseStartSeconds(search);
  const source = new URLSearchParams(search).get("utm_source") || undefined;

  useEffect(() => {
    if (!videoId) {
      return;
    }
    // A /link/* arrival was already recorded by LinkResolver with the same
    // query string; recording it again overwrites it with the same values.
    captureAttribution(search);
    logYouTubeLandingEvent("youtube_landing_view", {
      video_id: videoId,
      catalogue_id: idKind === "uuid" ? id : undefined,
      id_kind: idKind,
      source,
    });
    // One view per video shown, not per re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId]);

  if (!idKind) {
    return <Navigate to="/" replace />;
  }

  if (!videoId) {
    if (idKind === "uuid" && !overviewSettled) {
      return (
        <DefaultLayout>
          <Box
            data-testid="youtube-landing-loading"
            sx={{ ...backgroundSx, textAlign: "center" }}
          >
            <CircularProgress color="primary" />
          </Box>
        </DefaultLayout>
      );
    }
    return <Navigate to="/" replace />;
  }

  const eventParams = { video_id: videoId, source };

  return (
    <DefaultLayout>
      <Seo
        title="Free to watch from FarmFLiX"
        description="Watch this FarmFLiX video free. FarmFLiX delivers fresh agricultural videos every week for the farming community."
        image={youTubeThumbnailUrl(videoId)}
        url={`https://farmflix.tv/youtube/${id}`}
      />
      <Box sx={backgroundSx}>
        <Container maxWidth="md">
          <Typography variant="h1" sx={headingSx}>
            Free to watch from FarmFLiX
          </Typography>

          <Box data-testid="youtube-player" sx={playerBoxSx}>
            <iframe
              title="FarmFLiX video on YouTube"
              src={buildYouTubeEmbedUrl(videoId, startSeconds)}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Box>

          <Box sx={actionsSx}>
            <Button
              data-testid="youtube-watch-on-youtube"
              variant="outlined"
              sx={outlinedButtonSx}
              href={buildYouTubeWatchUrl(videoId)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                logYouTubeLandingEvent("youtube_watch_on_youtube", eventParams)
              }
            >
              Watch on YouTube
            </Button>
          </Box>

          {accountSettled && (
            <Box data-testid="youtube-upsell" sx={upsellSx}>
              {loggedIn ? (
                <>
                  <Typography
                    variant="h2"
                    sx={{ ...headingSx, fontSize: "1.4rem" }}
                  >
                    There&apos;s more on FarmFLiX
                  </Typography>
                  <Typography sx={bodySx}>
                    Your membership has fresh farming videos every week, on the
                    website and in the FarmFLiX app.
                  </Typography>
                  <Box sx={actionsSx}>
                    <Button
                      data-testid="youtube-join"
                      component={Link}
                      to="/videos"
                      variant="contained"
                      sx={buttonSx}
                      onClick={() =>
                        logYouTubeLandingEvent(
                          "youtube_join_click",
                          eventParams,
                        )
                      }
                    >
                      Browse FarmFLiX
                    </Button>
                    <Button
                      data-testid="youtube-get-app"
                      component={Link}
                      to="/app"
                      variant="outlined"
                      sx={outlinedButtonSx}
                      onClick={() =>
                        logYouTubeLandingEvent(
                          "youtube_get_app_click",
                          eventParams,
                        )
                      }
                    >
                      Get the app
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Typography
                    variant="h2"
                    sx={{ ...headingSx, fontSize: "1.4rem" }}
                  >
                    Like this? There&apos;s plenty more on FarmFLiX
                  </Typography>
                  <Typography sx={bodySx}>
                    FarmFLiX delivers fresh agricultural videos every week for
                    the farming community, on the website and in the app.
                  </Typography>
                  <Box sx={actionsSx}>
                    <Button
                      data-testid="youtube-join"
                      component={Link}
                      to="/try"
                      variant="contained"
                      sx={buttonSx}
                      onClick={() =>
                        logYouTubeLandingEvent(
                          "youtube_join_click",
                          eventParams,
                        )
                      }
                    >
                      Try FarmFLiX
                    </Button>
                    <Button
                      data-testid="youtube-get-app"
                      component={Link}
                      to="/app"
                      variant="outlined"
                      sx={outlinedButtonSx}
                      onClick={() =>
                        logYouTubeLandingEvent(
                          "youtube_get_app_click",
                          eventParams,
                        )
                      }
                    >
                      Get the app
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}
        </Container>
      </Box>
    </DefaultLayout>
  );
};

export default YouTubeLanding;
